import React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useMatch, useSearchParams } from 'react-router-dom';

import { useAppDispatch, useAppSelector } from '@/store';
import { creditCostFor, fetchCreditsPricing } from '@/store/slices/aiCredits';

import { Button, EmptyState, Panel, SectionTitle, Skeleton, Toast } from '../../components';
import { ftuxSignal } from '../../features/ftux/signals';
import { useFtuxViewIs } from '../../features/ftux/store/selectors';
import { useToast } from '../../hooks/useToast';
import { ROUTE } from '../../routes';
import { ART_STYLE } from '../../theme/tokens';
import { useLocalInternalSite } from '../../layouts/MainLayout/components/TopBar/useLocalInternalSite';
import { WebsitePage } from '../website/components/WebsiteShell';
import { primaryBrandLogoUrl } from '../creatives/brand-logo';
import { resolveSiteUrl } from '../creatives/live-preview';
import { BuyCreditsSheet } from '../creatives/components/BuyCreditsSheet';
import { ART_STYLE_SLOTS, SITE_REGEN_PRICE_KEY, type ArtStyleSlot } from './art-style-api';
import { isActiveJob, samplesSinceExtraction } from './sample-jobs';
import { isLookAction, isRunningPhase, lookInFlight, siteLookOf, sitePreviewUrl } from './site-regen';
import {
  livePrimaryStyleId,
  liveStyleIdOf,
  lookLabel,
  resolveSelection,
  selectionKey,
  stripEntries,
  type ArtStyleSelection,
} from './site-looks';
import { useArtStyle } from './useArtStyle';
import { useSiteRegen } from './useSiteRegen';
import { useLookSamples, useSiteLooks } from './useSiteLooks';
import { useSplitArrival } from './useSplitArrival';
import { ART_MORPH, useIngredientsMorph } from './useIngredientsMorph';
import { artStyleLabel } from './useActiveArtStyle';
import { useArtStyles } from './useArtStyles';
import { ArtStyleHeader } from './components/ArtStyleHeader';
import { DraftPlaceholder } from './components/DraftPlaceholder';
import { ExtractBar } from './components/ExtractBar';
import { IngredientsBody, type IngredientKey } from './components/IngredientsBody';
import { IngredientsHeading } from './components/IngredientsHeading';
import { IngredientsSheet } from './components/IngredientsSheet';
import { PreviewsPanel, SamplesPanel } from './components/SamplesPanel';
import { StylesStrip } from './components/StylesStrip';
import { RegenerateSheet } from './components/RegenerateSheet';
import { SiteRegenCard } from './components/SiteRegenCard';
import { SiteLookView } from './components/SiteLookView';
import { LookActionSheet, type LookAction } from './components/LookActionSheet';
import {
  Page,
  StripSkeleton,
  Studio,
  StudioColumn,
  CardHeader,
  IngredientsColumn,
  StudioDivider,
  Enter,
  SkeletonStack,
  Failure,
  MorphStyles,
} from './art-style.styles';

export type ArtStylePageProps = {
  styleText?: string;
  className?: string;
};

/** The price-table key of one sample draw (spark-owned). */
const SAMPLE_ACTION = 'art_style_sample';

/** The URL query naming the open style (`?style=<id>`). */
const STYLE_PARAM = 'style';
/** The URL query naming the open catalog look (`?look=<id>`); it and `?style=` never stand together. */
const LOOK_PARAM = 'look';
/** The query the brand's site reads to show a look before it is switched to it. */
const PREVIEW_QUERY = 'site_art_style_banner_set_id';

/** A website-look change waiting for the owner's confirmation: a catalog look drawn or switched to, or the site
 *  switched back to the brand's own look (drawn from one of its styles). */
type LookSheet = { action: LookAction; styleId: string; name: string; owner: boolean };

/**
 * Website → Art Style (`/account/website/art-style`), behind the `art-style` flag (generally available, kept as a
 * rollback switch).
 *
 * The owner defines the brand's art styles, each from its existing logo (read-only here, managed on the Logos
 * tab), a mood board, a written direction and an optional cast of named mascots (Lumio names and describes each). Lumio extracts a style definition from those
 * (free), draws four sample formats in it (charged per sample), and while a style is in use, every new banner
 * and image spark-creatives draws for the brand follows it. A brand keeps any number of styles (the strip on
 * top switches between them, `?style=<id>` names the open one) and uses at most one. Kept by spark-creatives
 * (see `./art-style-api` and the specs `.claude/specs/2026-09-30-owner-art-style.md` and
 * `.claude/specs/2026-10-01-art-style-multi.md`).
 *
 * This file only orchestrates: the list lives in `./useArtStyles`, the open style's record, autosave and
 * actions in `./useArtStyle`, each section in `./components`.
 */
const ArtStylePage = ({ styleText = '', className = '' }: ArtStylePageProps): React.ReactElement => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { toast, show } = useToast();

  const displayBrand = useAppSelector((state) => state.casinoInstance.displayBrand);
  const brandsLoaded = useAppSelector((state) => state.casinoInstance.loaded);
  const tenantRaw = Number(displayBrand?.tenant_id);
  const tenantId = Number.isFinite(tenantRaw) && tenantRaw > 0 ? tenantRaw : undefined;
  const brandLogoUrl = primaryBrandLogoUrl(displayBrand?.casino_data?.details?.logo);
  const brandDomain = displayBrand?.casino_data?.details?.domain ?? '';

  // Buy credits: opened by a draw or a site redraw the balance can't cover (pre-flight) or by the service's 402.
  const [buy, setBuy] = React.useState<{ open: boolean; suggested?: number }>({ open: false });
  // The Ingredients accordion keeps one section open at a time (the mood board first); clicking the open one closes it.
  const [openSection, setOpenSection] = React.useState<IngredientKey | null>('mood');
  // The "Regenerate website art" sheet (opened from the header once the style is primary), or "Set as primary" for a
  // style the site does not show (`adopt`: the style is drawn across the site, then the site switches to it).
  const [regen, setRegen] = React.useState<'regenerate' | 'adopt' | null>(null);
  // The confirmation of a website-look change (a catalog look's Generate or Set as primary, a switch back).
  const [lookSheet, setLookSheet] = React.useState<LookSheet | null>(null);
  const toggleSection = (key: IngredientKey): void => setOpenSection((current) => (current === key ? null : key));
  const openBuy = React.useCallback((needed?: number): void => {
    setBuy({ open: true, suggested: needed && needed > 0 ? needed : undefined });
  }, []);

  // The per-action price table drives the cost on every draw control and the pre-flight gate.
  React.useEffect(() => {
    void dispatch(fetchCreditsPricing());
  }, [dispatch]);
  const pricing = useAppSelector((state) => state.aiCredits?.pricing ?? null);
  const balance = useAppSelector((state) => state.ownerWallet?.credits ?? null);
  // An extraction (and the samples it draws) is a job: until the jobs feed has been read once, the page can't
  // tell whether the open style is being extracted, so it waits rather than flash the idle state.
  const jobsLoaded = useAppSelector((state) => state.creativeJobs.loaded);
  const perSampleCost = creditCostFor(pricing, SAMPLE_ACTION);
  const allSamplesCost = perSampleCost != null ? perSampleCost * ART_STYLE_SLOTS.length : undefined;
  // A full-site redraw ("Regenerate website art", a style adopted across the site, a catalog look's "Generate art in
  // this style"); switching, reverting and a Retry that resumes a failed job are free.
  const siteRegenCost = creditCostFor(pricing, SITE_REGEN_PRICE_KEY);

  // The brand's website look and its site jobs (regenerate, switch, revert, a catalog look's draw or switch):
  // brand-wide, whichever card is open.
  const site = useSiteRegen({ tenantId, notify: show, onNeedCredits: openBuy });
  const siteState = site.state;
  const siteRunning = siteState !== null && isRunningPhase(siteState.phase);
  const siteLocked = siteRunning || site.requesting !== null;
  // The site job's moves (its job and phase): the looks, a look's samples and the styles' statuses follow them.
  const siteKey = siteState ? `${siteState.job_id}:${siteState.phase}` : '';
  // The brand's catalog looks (null when the service has none here: then the page works as before them).
  const looks = useSiteLooks(tenantId, siteKey, siteRunning);
  const catalog = looks.status === 'ok' ? looks.looks : null;
  const liveStyleId = catalog ? liveStyleIdOf(siteState, catalog) : '';

  // The brand's styles. A brand with none gets a blank draft only when it has no catalog looks to show instead (and
  // waits until that is known).
  const list = useArtStyles(
    tenantId,
    looks.status === 'loading' ? undefined : !(catalog !== null && catalog.looks.length > 0),
  );
  // The custom style the website shows (its card is the Primary one). While what the site shows is not known (no
  // catalog looks here, or the service can't tell), the active style is, as before the catalog looks.
  const primaryStyleId = liveStyleId
    ? livePrimaryStyleId(siteState, liveStyleId)
    : list.items?.find((item) => item.status === 'active')?.id;
  const [params, setParams] = useSearchParams();
  // The URL is this page's only while it is the open route: leaving, the outgoing page stays mounted through the slide
  // under the next route's location (the transitions do not freeze it), and defaulting then wrote ?style= onto that
  // page's URL. Off the route it keeps the card it was showing and leaves the URL alone.
  // Matched as the router matches (a trailing slash or another case is still this page).
  const onRoute = useMatch(ROUTE.artStyle()) !== null;
  const onRouteRef = React.useRef(onRoute);
  onRouteRef.current = onRoute;
  // The entry's router state, which a replace keeps: it carries the Website hub's mark, which the area's back button
  // pops back to the hub by. Read through a ref so `select` keeps its identity as the location changes.
  const { state: entryState } = useLocation();
  const entryStateRef = React.useRef(entryState);
  entryStateRef.current = entryState;
  // Which card is open: the style or look the URL names when it is still the brand's, else the default (the Primary
  // card, else the active style, else the most recently updated), which the URL then takes over.
  const shownRef = React.useRef<ArtStyleSelection | undefined>(undefined);
  const resolved = resolveSelection({
    requestedStyle: params.get(STYLE_PARAM) ?? '',
    requestedLook: params.get(LOOK_PARAM) ?? '',
    styles: list.failure ? [] : list.items,
    looks: catalog,
    looksStatus: looks.status,
    siteSettled: site.settled,
    liveStyleId,
    primaryStyleId,
  });
  const selection = onRoute ? resolved.selection : shownRef.current;
  const fallback = onRoute ? resolved.fallback : undefined;
  const selectedId = selection?.kind === 'style' ? selection.id : undefined;
  const openLook =
    selection?.kind === 'look' ? catalog?.looks.find((look) => look.style_id === selection.id) : undefined;
  React.useEffect(() => {
    if (onRoute) shownRef.current = selection;
  }, [onRoute, selection]);

  const select = React.useCallback(
    (next: ArtStyleSelection, replace = false): void => {
      // Every write goes through here, a late create or duplicate included: none once the page is off its route.
      if (!onRouteRef.current) return;
      setParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(next.kind === 'look' ? LOOK_PARAM : STYLE_PARAM, next.id);
          params.delete(next.kind === 'look' ? STYLE_PARAM : LOOK_PARAM);
          return params;
        },
        // A new entry (the owner picked a card) starts without it: popping from there lands on the previous card.
        { replace, state: replace ? entryStateRef.current : undefined },
      );
    },
    [setParams],
  );
  const selectStyle = React.useCallback(
    (styleId: string, replace = false): void => select({ kind: 'style', id: styleId }, replace),
    [select],
  );

  // Defaulting (first visit, a stale or another brand's id, a deleted style) replaces the history entry, so
  // Back never lands on a card that is not there.
  const fallbackKind = fallback?.kind;
  const fallbackId = fallback?.id;
  React.useEffect(() => {
    if (fallbackKind && fallbackId) select({ kind: fallbackKind, id: fallbackId }, true);
  }, [fallbackKind, fallbackId, select]);

  const art = useArtStyle({
    tenantId,
    styleId: selectedId,
    logoUrl: brandLogoUrl,
    notify: show,
    onNeedCredits: openBuy,
    onRecord: list.adopt,
    onListStale: list.refreshSoon,
  });
  const { style } = art;

  // Whether a regeneration now would replace what players see (the sheet says so before confirming).
  const siteLook = siteLookOf(siteState);
  // The brand's site address (its internal host on a local Studio only, see useLocalInternalSite), for the preview.
  const localSite = useLocalInternalSite(tenantId, brandDomain);
  const siteBase = resolveSiteUrl(localSite ?? brandDomain);
  /** The brand's site showing a catalog look before it is switched to it. */
  const lookPreviewUrl = (styleId: string): string =>
    sitePreviewUrl(siteBase, `${PREVIEW_QUERY}=${encodeURIComponent(styleId)}`);
  const siteJobLook = siteState && isLookAction(siteState.action) ? siteState.target_style_id : '';
  const siteJobLookName = siteJobLook ? catalog?.looks.find((look) => look.style_id === siteJobLook)?.name ?? '' : '';
  let previewUrl = '';
  if (siteJobLook && siteState?.phase === 'ready') previewUrl = lookPreviewUrl(siteJobLook);
  else if (siteState && !siteJobLook) previewUrl = sitePreviewUrl(siteBase, siteState.preview_param);
  // The brand's active style: the open one when it is, else the list's (a Retry regenerates it from any style).
  const activeStyle =
    style?.status === 'active'
      ? { id: style.id, name: style.name }
      : list.items?.find((item) => item.status === 'active');
  const drawnStyle = list.items?.find((item) => item.id === siteState?.spark_style_id);

  /** Whether the balance covers a full-site redraw before one is asked for; when it clearly can't, Buy credits opens
   *  preselecting the gap and nothing starts. Same rule as `draw`: while the balance or the price is unknown nothing is
   *  blocked (the service's 402 stays authoritative). Checked before anything changes, so a short owner's click leaves
   *  the style, its edits and the site as they were. */
  const coversSiteRegen = (): boolean => {
    if (siteRegenCost != null && balance != null && balance < siteRegenCost) {
      openBuy(siteRegenCost - balance);
      return false;
    }
    return true;
  };

  /** "Regenerate website art" in the active style (charged). It reads the SAVED style, so the open one's pending edits
   *  go first when it is the active one. */
  const regenerateSite = async (): Promise<void> => {
    if (!activeStyle) {
      show(t('Set a style as primary first.'));
      return;
    }
    if (!coversSiteRegen()) return;
    if (style?.id === activeStyle.id && !(await art.flush())) {
      show(t("Your latest changes couldn't be saved. Please try again."));
      return;
    }
    await site.regenerate(activeStyle.id);
  };

  /** "Set as primary" for the open style while the site does not show it: when its art is the site's newest own look
   *  (drawn from it, still current), the site switches to it; otherwise the style is drawn across the site first (the
   *  sheet says so). */
  const setStyleAsPrimary = (): void => {
    if (!style || siteLocked) return;
    if (
      siteState &&
      siteState.spark_style_id === style.id &&
      siteState.owner_drawn &&
      !siteState.spark_style_outdated
    ) {
      setLookSheet({
        action: 'apply',
        styleId: style.id,
        name: artStyleLabel(style.name) || t('Your art style'),
        owner: true,
      });
      return;
    }
    setRegen('adopt');
  };

  /** The adopt sheet's confirm: put the open style in use (it is what the site is drawn from), then draw the whole site
   *  in it and switch the site to it. The draw is charged, so the balance is checked before the style is put in use. */
  const adoptStyle = async (): Promise<void> => {
    if (!style || !coversSiteRegen()) return;
    if (style.status !== 'active' && !(await art.setActive(true))) return;
    if (!(await art.flush())) {
      show(t("Your latest changes couldn't be saved. Please try again."));
      return;
    }
    await site.regenerate(style.id);
  };

  /** A confirmed website-look change. Only drawing a look's art is charged; switching to a look is free. */
  const confirmLook = (sheet: LookSheet): void => {
    if (sheet.owner) void site.switchSite();
    else if (sheet.action === 'draw') {
      if (coversSiteRegen()) void site.drawLook(sheet.styleId);
    } else void site.applyLook(sheet.styleId);
  };

  /** Retry the failed site job where it stopped (only the parts it did not finish run again), which is free; when it
   *  failed before casino-creation took it (its charge was refunded), run its action again, which is a new start and
   *  charged again for a draw, so a draw is gated on the balance like the first one (`regenerateSite` gates its own). */
  const retrySite = (): void => {
    const action = siteState?.action;
    const target = siteState?.target_style_id ?? '';
    void site.resume().then((resumed) => {
      if (resumed) return;
      if (action === 'switch') void site.switchSite();
      else if (action === 'revert') void site.revertSite();
      else if (action === 'look_draw' && target) {
        if (coversSiteRegen()) void site.drawLook(target);
      } else if (action === 'look_apply' && target) void site.applyLook(target);
      else void regenerateSite();
    });
  };

  // A site job that moved can change which style is in use (a catalog look going live pauses it, the brand's own look
  // going live puts its style back in use): the strip's styles are read again.
  const { refreshSoon: refreshListSoon } = list;
  const seenSiteKeyRef = React.useRef('');
  React.useEffect(() => {
    const seen = seenSiteKeyRef.current;
    seenSiteKeyRef.current = siteKey;
    if (seen && seen !== siteKey && !siteRunning) refreshListSoon();
  }, [siteKey, siteRunning, refreshListSoon]);

  // The open look's own art (read again as its site job moves, so a finished draw shows its art).
  const lookSamples = useLookSamples(tenantId, openLook?.style_id ?? '', siteKey);
  // The open style was deleted elsewhere: re-read the list, which drops it and opens the default. Should the
  // list still name it, the page stops waiting and offers Try again.
  const { refresh: refreshList } = list;
  const [missingChecked, setMissingChecked] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (art.loadFailure !== 'missing' || !selectedId) return;
    const checked = selectedId;
    void refreshList().then(() => setMissingChecked(checked));
  }, [art.loadFailure, selectedId, refreshList]);

  /** One create at a time, counting the save a duplicate waits for before the list's own flag is up. */
  const creatingRef = React.useRef(false);

  /** A blank draft, or a duplicate of `fromStyleId` (its saved inputs and definition, not its samples). */
  const createStyle = async (fromStyleId?: string): Promise<void> => {
    if (creatingRef.current || list.creating) return;
    creatingRef.current = true;
    // A duplicate copies what is saved, so the open style's pending edits go first.
    if (fromStyleId && fromStyleId === selectedId) await art.flush();
    const record = await list.create(fromStyleId);
    creatingRef.current = false;
    if (!record) {
      show(
        fromStyleId
          ? t("The style couldn't be duplicated. Please try again.")
          : t("A new style couldn't be started. Please try again."),
      );
      return;
    }
    art.prime(record);
    selectStyle(record.id);
  };

  const deleteStyle = async (styleId: string): Promise<boolean> => {
    const open = styleId === selectedId;
    // Nothing more is saved to a style on its way out.
    if (open) art.cancelPendingSave();
    const gone = await list.remove(styleId);
    if (!gone) {
      show(t("The style couldn't be deleted. Please try again."));
      if (open) art.retrySave();
      return false;
    }
    show(t('Style deleted.'));
    return true;
  };

  /** Draw samples, unless the balance clearly can't cover them (then Buy credits, preselecting the gap).
   *  While the balance or the price is unknown nothing is blocked: the service's 402 stays authoritative. */
  const draw = (slots?: readonly ArtStyleSlot[]): void => {
    const count = slots?.length ?? ART_STYLE_SLOTS.length;
    const cost = perSampleCost != null ? perSampleCost * count : undefined;
    if (cost != null && balance != null && balance < cost) {
      openBuy(cost - balance);
      return;
    }
    void art.drawSamples(slots);
  };

  /** Extract the style: the extraction itself is free, but its job then draws the four samples, which are what it
   *  costs, so it is gated on the balance like a draw of all four. Everything after the request (extracting, the
   *  definition landing, the samples drawing) follows from the jobs feed, so it survives a reload or a switch. */
  const extractStyle = (): void => {
    if (!style?.id) return;
    if (allSamplesCost != null && balance != null && balance < allSamplesCost) {
      openBuy(allSamplesCost - balance);
      return;
    }
    void art.extract();
  };

  const resolvingBrand = !tenantId && !brandsLoaded;
  const waitingForStyle =
    Boolean(tenantId) &&
    !list.failure &&
    !openLook &&
    (!list.items ||
      !selectedId ||
      art.loading ||
      !jobsLoaded ||
      (art.loadFailure === 'missing' && missingChecked !== selectedId));

  // The style the page shows (none while it loads or fails), and where it stands.
  const shownStyle =
    !resolvingBrand && !waitingForStyle && tenantId && !list.failure && !art.loadFailure && !openLook
      ? style
      : undefined;
  const hasDefinition = Boolean(shownStyle && shownStyle.definition.trim() !== '');

  // Before its first extraction a style's ingredients live in a sheet over the card. Opening such a style (a new one,
  // a switch, a load) opens its sheet, once per opening, unless Lumio is extracting it (the card says so instead);
  // closing it leaves the card's button to open it again. Kept per style id, adjusted as the style changes.
  const [sheet, setSheet] = React.useState<{ styleId: string; open: boolean } | null>(null);
  if (shownStyle && sheet?.styleId !== shownStyle.id) {
    setSheet({ styleId: shownStyle.id, open: !hasDefinition && !art.extracting });
  }
  const sheetOpen = shownStyle !== undefined && sheet?.styleId === shownStyle.id && sheet.open;
  const setSheetOpen = React.useCallback((styleId: string, open: boolean): void => setSheet({ styleId, open }), []);
  // FTUX: the art style piece follows the sheet as it opens and closes, the page leaving with it open included (a
  // no-op outside an FTUX step), and holds New style in its hovered look while it lights it.
  React.useEffect(() => {
    if (!sheetOpen) return undefined;
    ftuxSignal('style-ingredients-opened');
    return () => ftuxSignal('style-ingredients-closed');
  }, [sheetOpen]);
  const newStyleLit = useFtuxViewIs('style-new');
  // While the sheet is open it holds the split back: a definition landing under it turns the sheet into the rail
  // (`landing`, then the morph lets the sheet go); with it closed, the split arrives on its own (useSplitArrival).
  const split = hasDefinition && !sheetOpen;
  const landing = hasDefinition && sheetOpen;
  const morph = useIngredientsMorph(landing, () => {
    if (shownStyle) setSheetOpen(shownStyle.id, false);
  });
  const arrival = useSplitArrival(shownStyle?.id, split, morph.morphingRef);

  let content: React.ReactNode;
  if (tenantId && openLook) {
    const lookPrimary = liveStyleId ? openLook.style_id === liveStyleId : openLook.live;
    const inFlight = siteRunning && siteState?.target_style_id === openLook.style_id;
    content = (
      // Re-keyed per look: switching settles the other look in fresh.
      <React.Fragment key={`look:${openLook.style_id}`}>
        <Enter $index={0}>
          <SiteLookView
            look={openLook}
            primary={lookPrimary}
            drawing={inFlight && siteState?.action === 'look_draw'}
            switching={inFlight && siteState?.action === 'look_apply'}
            job={siteState}
            samples={lookSamples.failed ? [] : lookSamples.samples}
            locked={siteLocked}
            previewUrl={openLook.art === 'ready' && !lookPrimary ? lookPreviewUrl(openLook.style_id) : ''}
            cost={siteRegenCost}
            onGenerate={() =>
              setLookSheet({ action: 'draw', styleId: openLook.style_id, name: lookLabel(openLook.name), owner: false })
            }
            onSetPrimary={() =>
              setLookSheet({
                action: 'apply',
                styleId: openLook.style_id,
                name: lookLabel(openLook.name),
                owner: false,
              })
            }
          />
        </Enter>
      </React.Fragment>
    );
  } else if (resolvingBrand || waitingForStyle) {
    content = (
      <SkeletonStack aria-busy="true">
        <Panel>
          <Studio>
            <StudioColumn>
              <SkeletonStack>
                <Skeleton width="40%" height={20} />
                <Skeleton block height={72} radius={14} />
                <Skeleton block height={120} radius={12} />
                <Skeleton block height={96} radius={12} />
              </SkeletonStack>
            </StudioColumn>
            <StudioDivider aria-hidden="true" />
            <StudioColumn>
              <SkeletonStack>
                <Skeleton width="25%" height={20} />
                <Skeleton block height={220} radius={12} />
              </SkeletonStack>
            </StudioColumn>
          </Studio>
        </Panel>
        <Panel>
          <SkeletonStack>
            <Skeleton width="35%" height={20} />
            <Skeleton block height={72} radius={14} />
          </SkeletonStack>
        </Panel>
      </SkeletonStack>
    );
  } else if (!tenantId) {
    content = (
      <Panel>
        <EmptyState text="Pick a brand to set its art style." />
      </Panel>
    );
  } else if (list.failure === 'unavailable' || art.loadFailure === 'unavailable') {
    content = (
      <Panel>
        <SectionTitle title={t('Art Style')} icon="palette" />
        <EmptyState text="Art styles are not available here yet." />
      </Panel>
    );
  } else if (list.failure) {
    content = (
      <Panel>
        <Failure>
          <EmptyState text="Your art styles couldn't be loaded." />
          <Button variant="outline" label={t('Try again')} icon="refresh" onClick={list.retry} />
        </Failure>
      </Panel>
    );
  } else if (art.loadFailure || !style) {
    content = (
      <Panel>
        <Failure>
          <EmptyState text="Your art style couldn't be loaded." />
          <Button variant="outline" label={t('Try again')} icon="refresh" onClick={art.retryLoad} />
        </Failure>
      </Panel>
    );
  } else {
    // A style written but with nothing drawn since (a duplicate, or an extraction whose samples could not start, e.g.
    // short of credits) has only Extract to draw its samples, so its footer stays even with its ingredients unchanged.
    // On the casino_creation pipeline "Regenerate samples" draws them instead; only a style written before it (no
    // canonical text) or one still to be rated needs an extraction first, so the footer stays for those.
    const casinoCreation = style.pipeline === 'casino_creation';
    const missingSamples =
      hasDefinition &&
      !art.extracting &&
      (casinoCreation
        ? style.art_style.trim() === '' || style.rating_needed
        : ART_STYLE_SLOTS.every(
            (slot) =>
              samplesSinceExtraction(style.samples[slot], style.extraction?.at).length === 0 &&
              !isActiveJob(art.slotJobs[slot]) &&
              !art.startingSlots[slot],
          ));
    // A style drawn before the casino_creation pipeline (no canonical text yet) keeps showing the slot samples its
    // panel would show until it has a preview batch. A canonical style always gets the previews panel: its "Draw
    // samples" is the only way to draw when an extraction's batch did not start (the Extract footer is hidden then).
    const previewsView =
      casinoCreation &&
      art.previewsView &&
      !(
        style.art_style.trim() === '' &&
        art.previewsView.batchId === '' &&
        !art.previewsView.inFlight &&
        ART_STYLE_SLOTS.some((slot) => samplesSinceExtraction(style.samples[slot], style.extraction?.at).length > 0)
      )
        ? art.previewsView
        : null;
    const started = style.mood_images.length > 0 || style.prompt.trim() !== '' || style.mascots.length > 0;
    content = (
      // Re-keyed per style: switching opens the other style's sections fresh (and they settle in again).
      <React.Fragment key={style.id}>
        <Enter $index={0}>
          {/* One container: what Lumio reads (left) and what it draws from it (right). */}
          <Panel>
            <CardHeader>
              <ArtStyleHeader
                name={style.name}
                status={style.status}
                primary={liveStyleId ? style.id === primaryStyleId : undefined}
                saveState={art.saveState}
                canActivate={style.definition.trim() !== ''}
                locked={art.busy !== null || art.extracting}
                siteBusy={siteLocked}
                primaryLocked={catalog ? siteLocked : false}
                onRename={(name) => art.edit({ name })}
                onRetrySave={art.retrySave}
                onSetPrimary={() => (catalog ? setStyleAsPrimary() : void art.setActive(true))}
                onRegenerate={() => setRegen('regenerate')}
                regenerateCost={siteRegenCost}
              />
            </CardHeader>
            {split ? (
              <Studio>
                <IngredientsColumn ref={arrival.railRef} data-art-morph={ART_MORPH.card}>
                  <IngredientsHeading title={t('Ingredients')} />
                  <IngredientsBody style={style} art={art} openSection={openSection} onToggleSection={toggleSection} />
                  <ExtractBar
                    style={style}
                    busy={art.busy}
                    extracting={art.extracting}
                    missingSamples={missingSamples}
                    lastFailed={art.extractFailed}
                    cost={allSamplesCost}
                    onExtract={extractStyle}
                  />
                </IngredientsColumn>
                <StudioDivider ref={arrival.dividerRef} aria-hidden="true" />
                <StudioColumn ref={arrival.samplesRef} data-art-morph={ART_MORPH.samples}>
                  {previewsView ? (
                    <PreviewsPanel
                      style={style}
                      view={previewsView}
                      busy={art.busy}
                      extracting={art.extracting}
                      cost={allSamplesCost}
                      extractFailed={art.extractFailed}
                      onRegenerate={() => draw()}
                    />
                  ) : (
                    <SamplesPanel
                      style={style}
                      slotJobs={art.slotJobs}
                      busy={art.busy}
                      onDraw={draw}
                      starting={art.startingSlots}
                    />
                  )}
                </StudioColumn>
              </Studio>
            ) : (
              // Before the first extraction: the ingredients are in their sheet; the card says what they are for.
              <DraftPlaceholder
                extracting={art.extracting || landing}
                started={started}
                onOpen={() => setSheetOpen(style.id, true)}
              />
            )}
          </Panel>
        </Enter>
      </React.Fragment>
    );
  }

  // The strip stays put while the editor below switches; it waits for the list and the looks on the first load only.
  // With catalog looks, the Primary card leads and the looks follow the styles; without them it is the styles alone.
  const entries = stripEntries({
    styles: list.items ?? [],
    looks: catalog?.looks ?? null,
    primaryStyleId,
    liveStyleId,
    busyLookId: lookInFlight(siteState),
  });
  let strip: React.ReactNode = null;
  if ((list.items || list.failure) && looks.status !== 'loading' && entries.length > 0) {
    strip = (
      <Enter $index={0}>
        <StylesStrip
          entries={entries}
          selectedKey={selectionKey(selection)}
          creating={list.creating}
          onSelect={(entry) => {
            const next: ArtStyleSelection =
              entry.kind === 'look' ? { kind: 'look', id: entry.look.style_id } : { kind: 'style', id: entry.style.id };
            if (selectionKey(next) !== selectionKey(selection)) select(next);
          }}
          onCreate={() => void createStyle()}
          onDuplicate={(styleId) => void createStyle(styleId)}
          onDelete={deleteStyle}
          newLit={newStyleLit}
        />
      </Enter>
    );
  } else if (tenantId && !list.failure) {
    strip = (
      <StripSkeleton aria-busy="true">
        <Skeleton width={ART_STYLE.CARD_WIDTH_PX} height={ART_STYLE.STRIP_SKELETON_PX} radius={14} />
        <Skeleton width={ART_STYLE.CARD_WIDTH_PX} height={ART_STYLE.STRIP_SKELETON_PX} radius={14} />
      </StripSkeleton>
    );
  }

  // The brand's latest site job, between the strip and the open style (it is the brand's, not the style's). Only while
  // it needs attention (running, ready to switch, failed): a settled look (live / original) is already the strip's
  // Primary card. A live site whose strip cannot offer the original look (the looks are unavailable or empty) keeps
  // the card, with "Back to the original look", so the original can always be put back.
  const liveWithoutLooks =
    siteState?.phase === 'live' && looks.status !== 'loading' && !(catalog !== null && catalog.looks.length > 0);
  const siteCard =
    tenantId !== undefined &&
    siteState &&
    siteState.phase !== 'idle' &&
    (siteState.phase !== 'live' || liveWithoutLooks) &&
    siteState.phase !== 'original' ? (
      <Enter $index={0}>
        <SiteRegenCard
          state={siteState}
          styleName={siteJobLook ? lookLabel(siteJobLookName) : artStyleLabel(drawnStyle?.name ?? '')}
          previewUrl={previewUrl}
          inPlace={site.inPlace}
          requesting={site.requesting}
          onSwitch={() => void site.switchSite()}
          onApplyLook={
            siteJobLook && siteJobLookName && siteJobLook !== liveStyleId
              ? () =>
                  setLookSheet({
                    action: 'apply',
                    styleId: siteJobLook,
                    name: lookLabel(siteJobLookName),
                    owner: false,
                  })
              : undefined
          }
          onRevert={liveWithoutLooks ? () => void site.revertSite() : undefined}
          onRetry={retrySite}
        />
      </Enter>
    ) : null;

  return (
    <WebsitePage styleText={styleText} className={className}>
      <Page>
        {strip}
        {siteCard}
        {content}
      </Page>
      {sheetOpen && shownStyle ? (
        <IngredientsSheet
          style={shownStyle}
          art={art}
          landing={landing}
          openSection={openSection}
          onToggleSection={toggleSection}
          cost={allSamplesCost}
          onExtract={extractStyle}
          onClose={() => setSheetOpen(shownStyle.id, false)}
        />
      ) : null}
      {buy.open ? (
        <BuyCreditsSheet
          suggestedCredits={buy.suggested}
          onClose={() => setBuy({ open: false })}
          onSuccess={() => show(t('Credits added to your balance.'))}
        />
      ) : null}
      {regen && style && !openLook ? (
        <RegenerateSheet
          styleName={artStyleLabel(style.name) || t('Your art style')}
          available={style.site_regen_available}
          busy={art.busy !== null || siteLocked}
          inPlace={siteLook === 'owner' ? true : siteLook === 'not_owner' ? false : undefined}
          adopt={regen === 'adopt'}
          cost={siteRegenCost}
          onConfirm={() => void (regen === 'adopt' ? adoptStyle() : regenerateSite())}
          onClose={() => setRegen(null)}
        />
      ) : null}
      {lookSheet ? (
        <LookActionSheet
          action={lookSheet.action}
          lookName={lookSheet.name || t('Untitled look')}
          pausesStyle={!lookSheet.owner && (list.items?.some((item) => item.status === 'active') ?? false)}
          busy={siteLocked}
          cost={siteRegenCost}
          onConfirm={() => confirmLook(lookSheet)}
          onClose={() => setLookSheet(null)}
        />
      ) : null}
      <Toast toast={toast} />
      <MorphStyles />
    </WebsitePage>
  );
};

export default ArtStylePage;
